/** @jsx jsx */

import { css, jsx } from '@emotion/core';
import React, { Fragment as _, useState, useEffect } from 'react';
import { colors } from 'styles/variables';
import { H1 as H1_v2, H2 as H2_v2 } from '../EditorUtilities/UI/Headings';
import CheckBox_v2 from '../EditorUtilities/UI/CheckBox';
import NumberPicker_v2 from '../EditorUtilities/UI/NumberPicker';
import ColorPicker_v2 from '../EditorUtilities/UI/ColorPicker';
import setBlock from 'services/apiHelpers/setBlockProps';
import pubSub from 'services/pubSub';
import EditorBtn from 'components/UI/EditorBtn';
import { ImageLibraryContext } from '../EditImage/EditImage';
import ImageLibrary from '../EditImage/ImageLibrary';
import { useSelector, useDispatch } from 'react-redux';
import { State } from 'store/store';
import Info from '../EditorUtilities/UI/Info';
import InputText from 'components/UI/InputText';

const { lGrey0 } = colors;

export const questionXofXDefaultText =
  'Question {{current_question}} of {{total_questions}}';

const EditAssessment = ({ block }: { block?: any }) => {
  //
  const { id: blockId } = block;
  const {
    backgroundImage,
    backgroundImageOpacity = 100,
    src,
    imageId,
    hiddenFromMenu = false,
    hiddenFromPortal = false,
    asAssessment = true,
    passMark,
    maxAttempts,
    answersWrappingAlignment = 'center',
    saveData = false,
    learnMoreResetsAssessment = false,
    backgroundBlur = 0,
    parallax = false,
    parallaxValue = 10,
    backgroundGradient = false,
    backgroundGradientFullHeight = false,
    backgroundSecondaryColor = '',
    backgroundGradientAngle = 0,
    //
    assessmentQuestionSubmitLabel,
    assessmentQuestionContinueLabel,
    questionXofXtext,
  } = block.props;

  const [topic, setTopic] = useState(null);
  const [imageLibrary, setImageLibrary] = useState(false);

  const { theme, assessments } = useSelector((state: State) => {
    //TODO : add interface / type to theme on state
    return state;
  });

  const dispatch = useDispatch();

  const backgroundColor =
    block.props.backgroundColor || theme.backgroundColor || '';

  const textColor = block.props.textColor || theme.textColor || '';

  useEffect(() => {
    const pS = pubSub.subscribe('UPDATE_TOPIC_ICON', $ => {
      setTopic($.topic);
      requestAnimationFrame(() => setImageLibrary(!imageLibrary));
    });

    return () => pS();
  }, []);

  return (
    <div
      style={{ marginTop: 16 }}
      //mt={3}
    >
      {[
        <ColorPicker_v2
          label="Background color"
          inColor={backgroundColor}
          outColor={color => {
            setBlock(block, {
              backgroundColor: color,
            });
          }}
        />,
        <div
          css={css({
            margin: '10px 0 0 0',
          })}
        >
          <ColorPicker_v2
            inColor={backgroundSecondaryColor}
            outColor={color =>
              setBlock(block, {
                backgroundSecondaryColor: color,
                backgroundGradient: color ? true : false,
              })
            }
            label="Secondary background colour"
          />
        </div>,

        <_>
          {backgroundGradient && (
            <_>
              <div
                css={css({
                  margin: '10px 0 0 0',
                })}
              >
                <NumberPicker_v2
                  value={backgroundGradientAngle}
                  min={0}
                  limit={360}
                  increment={5}
                  onChange={value => {
                    setBlock(block, { backgroundGradientAngle: value });
                  }}
                  label="Gradient angle"
                />
              </div>
              <div
                css={css({
                  margin: '10px 0 0 0',
                })}
              >
                <CheckBox_v2
                  label={'Gradient spans full topic height'}
                  on={backgroundGradientFullHeight}
                  onClick={() => {
                    setBlock(block, {
                      backgroundGradientFullHeight: !backgroundGradientFullHeight,
                    });
                  }}
                />
              </div>
            </_>
          )}
        </_>,
        <ColorPicker_v2
          label="Text color"
          inColor={textColor}
          outColor={color => {
            setBlock(block, {
              textColor: color,
            });
          }}
        />,

        <EditorBtn
          label="Insert background image"
          style={{
            width: `${'175px'}`,
            margin: '0 0 15px 0',
            border: `1px solid ${lGrey0}`,
            background: '#fff',
            color: lGrey0,
            fontSize: 11,

            hover: {
              color: '#fff',
              background: '#28292b',
            },
          }}
          css={css`
            min-height: 30px;
          `}
          onClick={() => {
            setImageLibrary(!imageLibrary);
          }}
        />,

        (backgroundImage || src) && (
          <div>
            <div
              css={css({
                margin: '10px 0 10px 0',
              })}
            >
              <CheckBox_v2
                label="Add a parallax effect"
                on={parallax}
                onClick={() => {
                  setBlock(block, { parallax: !parallax });
                }}
              />
            </div>
            {parallax && (
              <div
                style={{
                  margin: '0 0 10px 0',
                }}
              >
                <NumberPicker_v2
                  value={parallaxValue}
                  // showValue={true}
                  min={0}
                  limit={100}
                  increment={5}
                  onChange={value => {
                    setBlock(block, { parallaxValue: value });
                  }}
                  label="Parallax strength"
                />
              </div>
            )}

            <NumberPicker_v2
              value={backgroundImageOpacity}
              min={0}
              limit={100}
              increment={5}
              onChange={value => {
                setBlock(block, { backgroundImageOpacity: value });
              }}
              label="Background image opacity"
            />

            <div
              style={{
                margin: '10px 0 0 0',
              }}
            >
              <NumberPicker_v2
                value={backgroundBlur}
                min={0}
                limit={100}
                increment={1}
                onChange={value => {
                  setBlock(block, { backgroundBlur: value });
                }}
                label="Background blur"
              />
            </div>
          </div>
        ),

        <div
          css={css`
            ${!src &&
              `
           opacity: 0.5;
           pointer-events: none;
           `}
          `}
          onClick={() => {
            setBlock(block, {
              src: undefined,
              imageId: undefined,
              ...(backgroundImage && {
                backgroundImage: '',
              }),
              backgroundImageOpacity: 100,
            });
          }}
        >
          <H2_v2
            css={css`
              color: #98add9;
              text-decoration: underline;
              cursor: pointer;
              &:hover {
                opacity: 0.8;
              }
            `}
          >
            Remove background image
          </H2_v2>
        </div>,
      ].map((child, i) => {
        return (
          <div
            key={`child_${i}`}
            css={css`
              margin: 0 0 10px 0;
            `}
          >
            {child}
          </div>
        );
      })}

      <CheckBox_v2
        label="Hide from menu"
        on={hiddenFromMenu}
        onClick={() => {
          setBlock(block, { hiddenFromMenu: !hiddenFromMenu });
        }}
      />

      <div
        css={css({
          margin: '10px 0 0 0',
        })}
      >
        <CheckBox_v2
          label="Hide from Topic Portal"
          on={hiddenFromPortal}
          onClick={() => {
            setBlock(block, { hiddenFromPortal: !hiddenFromPortal });
          }}
        />
      </div>

      <div
        css={css({
          display: 'flex',
          margin: '10px 0 0 0',
        })}
      >
        <CheckBox_v2
          label="Learn more navigation resets assessment"
          on={learnMoreResetsAssessment}
          onClick={() => {
            setBlock(block, {
              learnMoreResetsAssessment: !learnMoreResetsAssessment,
            });
          }}
        />

        {/* <Info info="When navigating to any learn more location the assessment will be reset on return.">
          Learn more navigation resets assessment
        </Info> */}
      </div>

      <div
        css={css({
          margin: '10px 0 0 0',
        })}
      >
        <CheckBox_v2
          label="Align answers left"
          on={answersWrappingAlignment === 'left'}
          onClick={() => {
            setBlock(block, {
              answersWrappingAlignment:
                answersWrappingAlignment === 'center' ? 'left' : 'center',
            });
          }}
        />
      </div>

      <div style={{ margin: '10px 0 0 0' }}>
        <NumberPicker_v2
          value={passMark}
          increment={1}
          limit={100}
          onChange={value => {
            setBlock(block, { passMark: value });
          }}
          label="Pass mark"
        />
      </div>

      <_>
        <div
          css={css`
            margin: 10px 0;
          `}
        >
          <H2_v2>Question submit button label</H2_v2>
          <InputText
            defaultValue={assessmentQuestionSubmitLabel || 'Submit'}
            onUpdate={value => {
              setBlock(block, { assessmentQuestionSubmitLabel: value });
            }}
            onEnter={value => console.log(value)}
            style={{ margin: 0 }}
          />
        </div>

        <div
          css={css`
            margin: 10px 0;
          `}
        >
          <H2_v2>Question continue button label</H2_v2>
          <InputText
            defaultValue={assessmentQuestionContinueLabel || 'Continue'}
            onUpdate={value => {
              setBlock(block, {
                assessmentQuestionContinueLabel: value,
              });
            }}
            onEnter={value => console.log(value)}
            style={{ margin: 0 }}
          />
        </div>
      </_>

      <div
        style={{
          margin: '10px 0 10px 0',
        }}
      >
        {/* <H2_v2>Question counter text</H2_v2> */}

        <Info
          info={
            <_>
              <div>
                <div>
                  You can add any text you like here, but the following
                  variables will be replaced with the actual values:
                  <ul
                    style={{
                      margin: '0 0 0 20px',
                    }}
                  >
                    <li>
                      <strong>{`{{current_question}}`}</strong> - The current
                      question number
                    </li>
                    <li>
                      <strong>{`{{total_questions}}`}</strong> - The total
                      number of questions
                    </li>
                  </ul>
                </div>
                <div
                  style={{
                    margin: '10px 0 0 0',
                  }}
                >
                  {`
Those variables will need to be included in the text you enter, so, for
                example, if you want the text to read "Question 1 of 10", you
                would enter "Question {{current_question}} of
                {{total_questions}}".`}
                </div>

                <div
                  style={{
                    margin: '10px 0 0 0',
                  }}
                >
                  {`The "{{" and "}}" are important, so
                please remember to include them.`}
                </div>
              </div>
            </_>
          }
        >
          Question counter text
        </Info>

        <InputText
          defaultValue={questionXofXtext || questionXofXDefaultText}
          onUpdate={value => {
            setBlock(block, {
              questionXofXtext: value,
            });
          }}
          onEnter={value => console.log(value)}
          onBlur={value => {
            if (!value) {
              setBlock(block, {
                questionXofXtext: questionXofXDefaultText,
              });
            }
          }}
          style={{ margin: 0 }}
        />
      </div>

      <div style={{ margin: '10px 0 0 0' }}>
        <NumberPicker_v2
          value={maxAttempts}
          min={1}
          increment={1}
          onChange={value => {
            setBlock(block, { maxAttempts: value });
          }}
          label="Max attempts"
        />
      </div>

      {imageLibrary && (
        <ImageLibraryContext.Provider
          value={{
            onAddImage(result) {
              setBlock(block, {
                src: result.url,
                imageId: result.id,
              });
            },
            onClose() {
              setImageLibrary(!imageLibrary);
            },
            currentImageSrc: src,
            currentImageId: imageId,
            cacheSelected: {
              yourLibrary: null,
              chameleonsLibrary: 0,
            },
            cacheFilter: {
              value: '',
            },
          }}
        >
          <ImageLibrary />
        </ImageLibraryContext.Provider>
      )}
    </div>
  );
};

export default EditAssessment;
